import {createDivIn, createImgIn, createLinkIn, setElementInnerHTML, setElementOnClick} from './ui_utils/DomUtils.ts'
import {EnumUtils} from '@colonist/utils'
import {UIImagePathController} from './UIEnums.ts'
import {getNestedTranslation, type TranslationKeys, type TranslationObject} from '@colonist/i18n'
import {ViewPopupStoreLink} from './ui_popup/ViewPopupStoreLink.ts'
import {PlayerColor} from '@colonist/game-states'
import {navigateToPage} from './ui_utils/ClientUtils.ts'
import {AudioManager, AudioType} from '@colonist/audio'
import {notificationController} from './EntryShared.ts'
import {ClientUserState} from './ClientUserState.ts'

interface PlayerColorState {
    activeColor: PlayerColor | undefined
    availableColors: PlayerColor[]
}

enum ColorSelectorType {
    Default,
    Room,
}

export class UIPlayerColorController {
    private static readonly colorStoreLink = '/store#colors'
    private userState: PlayerColorState
    private readonly colorSelectorType: ColorSelectorType

    protected colorsContainer: HTMLDivElement
    protected colorsGrid: HTMLDivElement

    protected selectColorAction: (color: PlayerColor) => Promise<void>

    constructor(container: HTMLDivElement, grid: HTMLDivElement, selectColorAction: (color: PlayerColor) => Promise<void>, selectedColor?: PlayerColor) {
        this.colorsContainer = container
        this.colorsGrid = grid
        this.selectColorAction = selectColorAction
        this.colorSelectorType = selectedColor == undefined ? ColorSelectorType.Default : ColorSelectorType.Room

        this.setState({
            activeColor: selectedColor ?? ClientUserState.userState?.preferredColor,
            availableColors: ClientUserState.userState?.items.getAvailableColors() ?? [],
        })
    }

    private setState(userState: PlayerColorState) {
        this.userState = userState
        this.createColors()
    }

    private async onColorSelect(color: PlayerColor) {
        AudioManager.play(AudioType.Click)
        await this.selectColorAction(color)

        if(this.colorSelectorType == ColorSelectorType.Room) {
            this.setState({
                activeColor: color,
                availableColors: ClientUserState.userState?.items.getAvailableColors() ?? [],
            })
            return
        }

        const primaryMessage: TranslationObject = {key: 'strings:homePage.header.defaultColorSelector.colorChanged'}
        const playerColorKey: TranslationKeys = `strings:colonistCommon.playerColors.${EnumUtils.getEnumKey(color, PlayerColor)}`
        const colorMessage: TranslationObject = {key: playerColorKey}
        const nestedTranslation = getNestedTranslation(primaryMessage, {color: colorMessage})
        const settlementImage = UIImagePathController.getPlayerColorBuildings(color).settlement

        notificationController.showNotification(settlementImage, nestedTranslation)
    }

    private createColors() {
        setElementInnerHTML(this.colorsGrid, {key: 'strings:colonistCommon.utils.emptyString'})
        EnumUtils.loopEnums(PlayerColor, (_: string, playerColor: PlayerColor) => {
            if(playerColor === PlayerColor.None) return
            this.createColorImage(playerColor)
        })
        this.showStoreLinkElement()
    }

    private createColorImage(color: PlayerColor) {
        const settlementLobbyImage = UIImagePathController.getPlayerColorBuildings(color).settlement
        const image = createImgIn(this.colorsGrid, settlementLobbyImage)
        setElementOnClick(image, this.imageOnclick(color))
        this.setColorImageStyling(color, image)
    }

    private imageOnclick(color: PlayerColor): (() => void) {
        if(this.canSelectColor(color)) return async() => await this.onColorSelect(color)
        return () => UIPlayerColorController.displayColorNotOwnedPopup(color)
    }

    private canSelectColor(color: PlayerColor): boolean {
        return this.userState.availableColors.includes(color)
    }

    private setColorImageStyling(color: PlayerColor, image: HTMLImageElement) {
        if(this.isColorUnavailable(color)) {
            image.classList.add('header_profile_color_transparent')
            return
        }
        if(this.isColorActive(color)) image.classList.add('active')
    }

    private isColorUnavailable(color: PlayerColor): boolean {
        if(this.canSelectColor(color)) return false
        if(color == PlayerColor.Black) return false
        return true
    }

    protected isColorActive(color: PlayerColor): boolean {
        return color == this.userState.activeColor
    }

    private static displayColorNotOwnedPopup(color: PlayerColor) {
        const defaultMessage = getNestedTranslation({key: 'strings:homePage.header.storePurchaseColorPrompt'}, {
            color: {key: `strings:colonistCommon.playerColors.${EnumUtils.getEnumKey(color, PlayerColor)}`},
        })
        const blackMessage: TranslationObject = {key: 'strings:homePage.header.defaultColorSelector.getBlackColorForFree'}
        new ViewPopupStoreLink({key: 'strings:homePage.header.defaultColorSelector.youDoNotOwnThisColor'}, color === PlayerColor.Black ? blackMessage : defaultMessage)
    }

    private showStoreLinkElement() {
        const storeLinkElementClass = 'header_profile_selector_noicons'
        const storeLinkElements = this.colorsContainer.getElementsByClassName(storeLinkElementClass)
        for(const storeLinkElement of storeLinkElements) {
            storeLinkElement.remove()
        }

        const linkContainer = createDivIn(this.colorsContainer, storeLinkElementClass)
        const link = createLinkIn(linkContainer, '', {key: 'strings:homePage.header.defaultColorSelector.getMoreColors'}, '')
        setElementOnClick(link, () => navigateToPage(UIPlayerColorController.colorStoreLink, true, true), true, true)
    }
}
